import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Search, Calendar, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { getClosedBoardMessages } from '@/lib/api';

export function ClosedMessagesDialog({ open, onOpenChange }) {
  const [messages, setMessages] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setLoading(true);
      getClosedBoardMessages()
        .then(setMessages)
        .catch(() => toast.error('Failed to load'))
        .finally(() => setLoading(false));
    }
  }, [open]);

  const filtered = messages.filter(m =>
    (m.content || '').toLowerCase().includes(search.toLowerCase()) ||
    (m.current_list_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (m.assigned_to_name || '').toLowerCase().includes(search.toLowerCase())
  );

  const fmtDateTime = (d) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
    catch { return d; }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="closed-messages-dialog">
        <DialogHeader>
          <DialogTitle className="text-[15px] font-semibold">Closed Conversations</DialogTitle>
        </DialogHeader>
        <div className="relative mb-3">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <Input className="pl-8 h-8 text-[13px]" placeholder="Search closed conversations..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="flex-1 overflow-y-auto space-y-2">
          {loading ? (
            <p className="text-center py-6 text-[13px] text-gray-400">Loading...</p>
          ) : filtered.length === 0 ? (
            <p className="text-center py-6 text-[13px] text-gray-400">No closed conversations</p>
          ) : filtered.map(msg => (
            <div key={msg.id} className="px-3 py-2.5 rounded-lg border hover:bg-gray-50" data-testid={`closed-msg-${msg.id}`}>
              <p className="text-[13px] text-gray-700 line-clamp-2">{msg.content}</p>
              <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                <span className="text-[11px] text-gray-500">To: {msg.assigned_to_name}</span>
                {msg.due_date && (
                  <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                    <Calendar className="h-3 w-3" /> {msg.due_date}
                  </span>
                )}
                {msg.responses?.length > 0 && (
                  <span className="text-[10px] text-gray-400">{msg.responses.length} response{msg.responses.length !== 1 ? 's' : ''}</span>
                )}
                <span className="text-[10px] text-gray-300">Closed {fmtDateTime(msg.closed_at)}</span>
              </div>
              {/* Show conversation thread */}
              {msg.responses?.length > 0 && (
                <details className="mt-2">
                  <summary className="text-[10px] text-gray-400 cursor-pointer hover:text-gray-600">Show conversation</summary>
                  <div className="space-y-1 mt-1">
                    {msg.responses.map((r, i) => (
                      <div key={i} className="text-[11px] px-2 py-1 bg-gray-50 rounded border border-gray-100">
                        <span className="font-medium text-gray-600">{r.by_name}</span>
                        <span className="text-gray-400"> to </span>
                        <span className="font-medium text-gray-600">{r.to_name}</span>
                        <span className="text-gray-400 ml-1.5">{fmtDateTime(r.created_at)}</span>
                        <p className="text-gray-500 mt-0.5">{r.text}</p>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
